// Light/dark theme preference (ADR-WBC1). The theme follows the operating
// system until the visitor picks one with the theme toggle; only that explicit
// choice is stored. (Before ADR-WBC1 the app stored whatever the first visit
// resolved to, which froze the OS setting of that moment for good.)
// Dependency-free: the head bootstrap (src/boot/head.ts) bundles this file to
// set <html data-theme> before the first paint, and the app reads the same
// function, so the two always agree.

export type Theme = "dark" | "light";

/** localStorage key of the visitor's explicit choice (unchanged since MERGED-UX-005). */
export const THEME_STORAGE_KEY = "tinkr-theme";

export const DARK_SCHEME_QUERY = "(prefers-color-scheme: dark)";

type ThemeWindow = Pick<Window, "localStorage" | "matchMedia">;

/** The stored explicit choice, or null (none, invalid, or storage blocked). */
export function storedTheme(win: ThemeWindow): Theme | null {
  try {
    const stored = win.localStorage.getItem(THEME_STORAGE_KEY);
    return stored === "dark" || stored === "light" ? stored : null;
  } catch {
    return null;
  }
}

/** The operating system's scheme; light when it cannot be read. */
export function systemTheme(win: Pick<Window, "matchMedia">): Theme {
  try {
    return typeof win.matchMedia === "function" && win.matchMedia(DARK_SCHEME_QUERY).matches ? "dark" : "light";
  } catch {
    return "light";
  }
}

/** The theme a page load starts with: the explicit choice, else the system scheme. */
export function initialTheme(win: ThemeWindow): Theme {
  return storedTheme(win) ?? systemTheme(win);
}

/** Records an explicit choice made with the theme toggle. */
export function rememberThemeChoice(win: Pick<Window, "localStorage">, theme: Theme): void {
  try {
    win.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Storage blocked: the choice still applies for this page.
  }
}
